/**
 * WebSmDifficulty — three escalating mission briefcases on the chosen handler's
 * desk (ported from Glow's WebSmDifficulty_type.jsx). Tapping a case takes it
 * through its code panel; once it unlocks, the step emits that case's click
 * and the open case is the next step (WebSmCase), where the mission is read
 * and accepted.
 *
 * The third file is sealed until two Challenging missions are filed
 * (CHALLENGING_TO_UNLOCK): it shows a lock on the
 * desk and its sheet explains why instead of offering the text button
 * (Diff3Unlocked=1 lifts the seal).
 *
 * Opening a case goes through its code panel, so the mission is only ever
 * handed to a verified number: a modal takes their number and texts a code,
 * then the view zooms onto the case's panel and the code is keyed in there. A
 * known operative (number on file, or in on one of our texts) is not asked for
 * it: the code goes straight to the number on file and the panel waits for it.
 *
 * Options: Title / Prompt / DeskImage / Diff{1,2,3}Label / Diff{1,2,3}Code
 *   / ChangeLabel / Diff3Unlocked / LockMessage / BgColor / AccentColor.
 * Emits CLICK1 / CLICK2 / CLICK3 (the unlocked case).
 */
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ButtonHTMLAttributes,
  type FormEvent,
} from "react";
import { flushSync } from "react-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ChevronDown } from "lucide-react";
import { useFunnelStore, useStepperStore } from "@/store";
import {
  useHasPhone,
  opLink,
  usePhoneLogin,
  type SignedIn,
  takeUpMission,
} from "@/features/funnel/hooks/use-phone-login";
import {
  fileKeys,
  useMissionFile,
} from "@/features/funnel/api/use-mission-file";
import { doorKeys } from "@/features/funnel/api/use-doors";
import type { Tier } from "@/features/funnel/types";
import { ACTION_BTN_CLASS, stepVars } from "./step-shell";
import { OPEN_CASES, tierOfLevel } from "./open-case";
import type { StepTypeProps } from "./types";
import {
  PHOTO_BOX,
  bubbleFor,
  deskFor,
  deskTopFor,
  notYetFor,
} from "./desk-art";
import { CASE_HIT_SHAPES } from "./case-hit-shapes";
import { usePressGroup } from "./use-press-group";
import caseBeginner from "./case-beginner.webp";
import caseChallenging from "./case-challenging.webp";
import caseImprobable from "./case-improbable.webp";

/** Folder order on the desk, as the tier each one resolves a mission at. */
const TIERS: Tier[] = ["beginner", "challenging", "improbable"];

/** Mission Improbable opens once this many Challenging missions are filed;
 *  until then its case is sealed and the handler waves it off. */
const CHALLENGING_TO_UNLOCK = 2;
/** How far along they are, said after the handler's line. */
const unlockProgress = (done: number) =>
  done === 0
    ? "You haven't done one yet."
    : `That's ${done} down, ${CHALLENGING_TO_UNLOCK - done} to go.`;

/** What each handler says when the sealed file is tapped. With no handler
 *  picked yet (missions 1-3), the agent at the desk says it. */
const NO_HANDLER_NOT_YET_LINE =
  "Bold. I like bold. But that file stays sealed until you've completed two Challenging missions. Pick one of the other two and show me what you've got.";

const NOT_YET_LINE: Record<string, string> = {
  veronica:
    "I admire the nerve. I do. But not that one, not yet — finish two missions at Challenging and I will put it back on the table myself.",
  darius:
    "I like the ambition, I do. But that file is the closing trick, not the opening one — two Challenging missions with steady hands, then we talk about the big one.",
};

/**
 * The desk band at the 390x844 phone the files are authored against. Files are
 * sized in the band's own units (see `--f-width`), so the arrangement is one
 * composition that fills the visible desk side to side on any screen.
 */
const REF_BAND = { w: 390, h: 523 };

/** The desk's tilt away from them, about its front edge. */
const DESK_TILT = (32 * Math.PI) / 180;

/**
 * Where a case stands on the desk, worked out flat (px in the stage, w x h):
 * the perspective the desk was drawn with — tilted back DESK_TILT, seen from
 * 2.82 widths away, from 40% of its height above it — applied by hand. The
 * cases used to stand on a 3D-tilted element, but Safari places taps on 3D
 * layers away from where it draws them (taps on Challenging landed on
 * Improbable); flat, the picture on screen is exactly what takes the tap.
 */
const placeOnDesk = (
  w: number,
  h: number,
  look: { width: number },
  pos: { anchor: "left" | "right" | "center"; inset: string; bottom: string },
) => {
  const fw = Math.min(
    (look.width / REF_BAND.w) * w,
    (look.width / REF_BAND.h) * h,
  );
  const pct = (v: string) => parseFloat(v) / 100;
  const x0 =
    pos.anchor === "left"
      ? pct(pos.inset) * w
      : pos.anchor === "right"
        ? w - pct(pos.inset) * w - fw
        : w / 2 - fw / 2 - pct(pos.inset) * w;
  // how far back along the desk its front edge sits, and how much nearer
  // things are drawn larger
  const back = pct(pos.bottom) * h;
  const eye = 2.82 * w;
  const scale = eye / (eye + back * Math.sin(DESK_TILT));
  const ox = w / 2;
  const oy = -0.4 * h;
  const y = h - back * Math.cos(DESK_TILT);
  return {
    left: ox + (x0 - ox) * scale,
    bottom: h - (oy + (y - oy) * scale),
    width: fw * scale,
  };
};

/**
 * A case on the desk: the art (trimmed to the case itself), its width on the
 * REF_BAND phone, and the colour of its lights, which the difficulty label on
 * the lid glows in.
 */
interface CaseLook {
  src: string;
  hitShape: string;
  width: number;
  /** a slight tilt, as if set down by hand (deg; negative leans left) */
  tilt: number;
  glow: string;
  /** width / height of the trimmed art */
  aspect: number;
  /** the code panel's label and input boxes, in % of the art (from
   *  scripts/build-case-art.py, which paints out the printed text there) */
  panel: { label: Box; input: Box };
}
type Box = { left: number; top: number; width: number; height: number };

const pct = (b: Box): CSSProperties => ({
  left: `${b.left}%`,
  top: `${b.top}%`,
  width: `${b.width}%`,
  height: `${b.height}%`,
});

/** How wide the zoomed case is drawn, in multiples of the frame (a phone's
 *  width, capped on desktop): close enough to key the code into its panel,
 *  far enough to still read as the case. */
const ZOOM = 2.4;
const FRAME = "min(100vw, 480px)";
/** ACCESS GRANTED stays up this long before the case opens. */
const GRANTED_MS = 1100;
/** How long they can look at the desk before we point at the cases. */
const HINT_MS = 8000;

type Stage = "phone" | "code" | "granted";
/** The texted code's length (secretmissions send_phone_code: six digits). */
const CODE_LEN = 6;

/** Their number, asked for before the case's code panel: the code it texts
 *  is what they key into the panel. */
const PhoneModal = ({
  login,
  onBack,
  onSubmit,
}: {
  login: ReturnType<typeof usePhoneLogin>;
  onBack: () => void;
  onSubmit: (e: FormEvent) => void;
}) => (
  <div className="absolute inset-0 z-[7] grid place-items-center bg-black/70 p-4 font-sans">
    <form
      onSubmit={onSubmit}
      className="max-h-full w-full max-w-[360px] animate-[smSay_.35s_cubic-bezier(.2,.8,.3,1)_both] overflow-y-auto overscroll-contain rounded-xl border border-[#d6a94e]/45 bg-[rgb(18_16_18/0.96)] px-[22px] pt-[22px] pb-[14px] shadow-[0_20px_60px_rgba(0,0,0,.55)]"
    >
      <h2 className="m-0 text-lg font-bold tracking-[0.2px] text-[#f4eddb]">
        Unlock your case
      </h2>
      <p className="mt-1.5 mb-4 text-[14px] leading-[1.4] text-[#a99d86]">
        We&apos;ll text you the code for it.
      </p>
      <label
        htmlFor="case-phone"
        className="mb-2 block text-xs tracking-[1px] text-[#a99d86] uppercase"
      >
        Phone
      </label>
      <input
        id="case-phone"
        autoFocus
        type="tel"
        inputMode="tel"
        autoComplete="tel"
        placeholder="555-555-5555"
        value={login.phone}
        onChange={(e) => login.setPhone(e.target.value)}
        className="w-full rounded-lg border border-[#d6a94e]/45 bg-black/35 px-[14px] py-3 text-base text-[#f4eddb] outline-none"
      />
      <button
        type="submit"
        disabled={login.busy || !login.phoneValid}
        className={`mt-[14px] w-full ${ACTION_BTN_CLASS} rounded-lg px-4 py-3 text-[14.5px] font-semibold disabled:cursor-not-allowed disabled:opacity-50`}
      >
        {login.busy ? "Sending…" : "Text me the code"}
      </button>
      <button
        type="button"
        onClick={onBack}
        className="mt-1 w-full cursor-pointer rounded-lg bg-transparent px-4 py-3 text-[14.5px] font-medium text-[#a99d86]"
      >
        Back to the desk
      </button>
    </form>
  </div>
);

/**
 * The chosen case, zoomed onto its code panel: the label and the input are
 * drawn onto the panel's screen in the case's own glow. The texted code goes
 * in; a verified number shows ACCESS GRANTED and opens the case.
 */
const CodePanel = ({
  look,
  stage,
  login,
  onBack,
  onChangeNumber,
  onVerify,
  unasked,
}: {
  /** the code went to the number on file: they did not type one */
  unasked: boolean;
  look: CaseLook;
  stage: Stage;
  login: ReturnType<typeof usePhoneLogin>;
  onBack: () => void;
  onVerify: (e: FormEvent) => Promise<unknown>;
  onChangeNumber: () => void;
}) => {
  const label = stage === "granted" ? "Access granted" : "Enter code:";
  const cx = look.panel.input.left + look.panel.input.width / 2 - 2;
  const cy = look.panel.input.top;
  const width = `calc(${FRAME} * ${ZOOM})`;
  // Focus only the real code field on mount. Do not refocus after every
  // request or park the keyboard in a hidden field between screens.
  const field = useRef<HTMLInputElement>(null);
  const submit = (e: FormEvent) => {
    (document.activeElement as HTMLElement | null)?.blur();
    return onVerify(e);
  };
  // A full code (pasted, autofilled from the text, or typed) goes straight in —
  // once per code, so a wrong one is not retried until it is changed.
  const tried = useRef("");
  useEffect(() => {
    const digits = login.code.replace(/\D/g, "");
    if (
      stage !== "code" ||
      login.busy ||
      login.attemptId == null ||
      digits.length !== CODE_LEN
    )
      return;
    if (tried.current === digits) return;
    tried.current = digits;
    field.current?.form?.requestSubmit();
  }, [login.code, login.busy, login.attemptId, stage]);
  return (
    <div className="absolute inset-0 z-[7] overflow-clip">
      <div
        onClick={stage === "code" ? onBack : undefined}
        className="absolute inset-0 animate-[smDim_.45s_ease_both] bg-black/75"
      />
      {/* A code they never asked for (we knew their number): say it is on
          its way, over the panel. Not when they typed their number in. The
          code field keeps the focus either way (it takes no taps). */}
      {stage === "code" && unasked ? (
        <div
          id="case-code-notice"
          role="status"
          className="pointer-events-none absolute inset-x-0 bottom-[calc(58%+64px)] z-[8] mx-auto w-[min(340px,calc(100vw-48px))] animate-[smSay_.35s_cubic-bezier(.2,.8,.3,1)_both] rounded-[9px] border border-[var(--f-glow,#d6a94e)]/40 bg-black/75 px-4 py-2.5 text-center text-[14.5px] leading-snug text-[#eadfc6]"
        >
          {login.attemptId != null && login.phone ? (
            <>
              We just texted a code to your number ending{" "}
              <b>{login.phone.replace(/\D/g, "").slice(-4)}</b>.
            </>
          ) : (
            "We’re texting a code to your saved number to unlock this case…"
          )}
        </div>
      ) : null}
      <form
        id="case-code"
        onSubmit={submit}
        style={
          {
            width,
            // the panel's centre on the screen's, a little above middle
            left: `calc(50% - ${width} * ${cx / 100})`,
            top: `calc(42% - ${width} / ${look.aspect} * ${cy / 100})`,
            transformOrigin: `${cx}% ${cy}%`,
            "--f-glow": look.glow,
          } as CSSProperties
        }
        className="absolute animate-[smZoom_.7s_cubic-bezier(.2,.8,.25,1)_both]"
      >
        <img src={look.src} alt="" decoding="async" className="block w-full" />
        <div
          style={{
            ...pct(look.panel.label),
            fontSize: `calc(${FRAME} * ${ZOOM} * .0105)`,
          }}
          className={`absolute flex items-center font-mono leading-none tracking-[.04em] whitespace-nowrap text-[var(--f-glow)] uppercase [text-shadow:0_0_8px_var(--f-glow)] ${
            stage === "granted" ? "animate-[smBlink_.35s_steps(2)_3]" : ""
          }`}
        >
          {label}
        </div>
        <input
          ref={field}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          aria-label="Verification code"
          aria-describedby={unasked ? "case-code-notice" : undefined}
          enterKeyHint="done"
          placeholder={stage === "granted" ? "" : "••••••"}
          // readOnly, not disabled: disabling a focused field drops the
          // keyboard with no blur and leaves the page shoved up.
          readOnly={stage !== "code" || (login.busy && login.attemptId != null)}
          value={stage === "granted" ? "" : login.code}
          onChange={(e) => login.setCode(e.target.value)}
          autoFocus
          style={{
            ...pct(look.panel.input),
            // 16px floor: iOS zooms the page into any smaller field
            fontSize: `max(16px, calc(${FRAME} * ${ZOOM} * .0092))`,
          }}
          className={`absolute border-0 bg-transparent px-[.3em] text-center font-mono tracking-[.04em] text-[var(--f-glow)] caret-[var(--f-glow)] outline-none [text-shadow:0_0_8px_var(--f-glow)] placeholder:text-[var(--f-glow)] placeholder:opacity-30`}
        />
      </form>
      {stage === "code" ? (
        <div className="absolute inset-x-0 bottom-[max(28px,6dvh)] z-[8] mx-auto flex w-[min(340px,calc(100vw-48px))] flex-col items-stretch gap-2.5">
          <button
            type="submit"
            form="case-code"
            disabled={login.busy}
            className={`${ACTION_BTN_CLASS} rounded-[9px] px-6 py-3 text-[17px] font-bold`}
          >
            {login.busy
              ? login.attemptId == null
                ? "Sending…"
                : "Checking…"
              : "Unlock"}
          </button>
          <button
            type="button"
            disabled={login.busy}
            onClick={onChangeNumber}
            className="cursor-pointer text-[14px] text-[#eadfc6]/80 underline underline-offset-2"
          >
            Use a different number
          </button>
          <button
            type="button"
            // always works, even mid-check: a check that answers after they
            // left changes nothing (the parent drops it — see verify)
            onClick={onBack}
            className="cursor-pointer text-[14px] text-[#eadfc6]/80 underline underline-offset-2"
          >
            Back to the desk
          </button>
        </div>
      ) : null}
    </div>
  );
};

const Case = ({
  caseIndex,
  look,
  label,
  width,
  press,
  disabled,
}: {
  caseIndex: number;
  look: CaseLook;
  label: string;
  /** drawn width, px (placeOnDesk) */
  width: number;
  press: ButtonHTMLAttributes<HTMLButtonElement>;
  disabled: boolean;
}) => {
  const vars = {
    // In units of the stage (see REF_BAND), so the cases keep one arrangement
    // at every screen size.
    "--f-width": `${width}px`,
    "--f-glow": look.glow,
    "--f-tilt": `${look.tilt}deg`,
  } as CSSProperties;
  return (
    <div
      style={vars}
      // pressed in the moment it is touched (before React has caught up),
      // and no tap delay on phones
      className="pointer-events-none relative w-[var(--f-width)] origin-bottom transition-[scale] duration-100 has-[button:active]:scale-[.96]"
    >
      {/* Its shadow, lying flat on the desk plane and turned with the case:
          a soft pool spreading out past its sides and front, and a dark
          contact band where it meets the wood. */}
      {/* (foreshortened, as lying on the tilted desk) */}
      <div className="pointer-events-none absolute inset-0 origin-bottom scale-y-[.85]">
        <div className="absolute inset-x-[-16%] bottom-[-38%] h-[85%] rotate-[var(--f-tilt)] rounded-[50%] bg-[radial-gradient(closest-side,rgba(0,0,0,.88),rgba(0,0,0,.55)_50%,rgba(0,0,0,.2)_78%,transparent)]" />
        <div className="absolute inset-x-[-5%] bottom-[-9%] h-[30%] rotate-[var(--f-tilt)] rounded-[50%] bg-[radial-gradient(closest-side,rgba(0,0,0,.95),rgba(0,0,0,.55)_65%,transparent)]" />
      </div>
      {/* the case art is already drawn in perspective: it sits on the desk,
          set down at a slight angle, foreshortened by the desk's tilt (as the
          3D desk drew it: cos 32deg) */}
      <div
        style={{ aspectRatio: look.aspect }}
        className="relative block w-full origin-bottom rotate-[var(--f-tilt)] scale-y-[.848] has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-4 has-[:focus-visible]:outline-[#eadfc6]"
      >
        {/* Art and hit target share one transformed frame. Only the target
            is clipped, preserving every painted edge and glow in the image. */}
        <button
          type="button"
          aria-label={label}
          data-press-index={caseIndex}
          disabled={disabled}
          {...press}
          style={{ clipPath: look.hitShape }}
          className="pointer-events-auto absolute inset-0 cursor-pointer touch-manipulation border-0 bg-transparent p-0 outline-none disabled:pointer-events-none"
        />
        <img
          src={look.src}
          alt=""
          draggable={false}
          decoding="async"
          className="pointer-events-none block w-full"
        />
        {/* the difficulty, stencilled on the lid in the colour of its lights */}
        <div className="pointer-events-none absolute top-[24%] left-1/2 w-[74%] -translate-x-1/2 -translate-y-1/2 text-center font-stencil text-[calc(var(--f-width)*.1)] leading-[1] tracking-[.06em] text-[var(--f-glow)] uppercase [text-shadow:0_0_10px_var(--f-glow),0_0_2px_rgba(0,0,0,.9),0_2px_6px_rgba(0,0,0,.9)]">
          {label}
        </div>
      </div>
    </div>
  );
};

export default function WebSmDifficultyType({
  getOpt,
  dispatch,
}: StepTypeProps) {
  const bg = getOpt("BgColor") || "#0a0809";
  const accent = getOpt("AccentColor") || "#d6a94e";
  // DeskImage is the authoring override; otherwise the desk follows the pick.
  const handler = useFunnelStore((s) => s.handler);
  // No codename is shown here. It names the MISSION, and the mission does not
  // exist until this tap — the claim happens on acceptance and the name goes
  // out in the text.
  // No handler yet (missions 1-3): the room with the agent working at the
  // back, who turns round to wave the sealed file off.
  const desk = deskFor(handler, getOpt("DeskImage"));
  const notYet = notYetFor(handler);
  // In photo coordinates (see PHOTO_BOX): where the files' band starts, and
  // the speech bubble.
  const deskTop = deskTopFor(handler);
  const bubble = bubbleFor(handler);

  const title = getOpt("Title") || "Choose Mission Difficulty";
  // `Prompt`, not `Subtitle`: the authored Subtitle on live adventures is the
  // old texting instruction, which now lives inside the opened file.
  const prompt = getOpt("Prompt") || "Select your mission case.";
  const changeLabel = getOpt("ChangeLabel") || "Choose a different file";
  const handlerKey = (handler || "veronica").toLowerCase();
  // Authored copy wins; otherwise the handler says it in their own voice.
  const lockLine =
    getOpt("LockMessage") ||
    (handler
      ? NOT_YET_LINE[handlerKey] || NOT_YET_LINE.veronica
      : NO_HANDLER_NOT_YET_LINE);
  // Sealed until two Challenging missions are filed (Diff3Unlocked=1 opens
  // it regardless); once open it is just another case, no "not yet".
  // Until the file is in, the case stays sealed but the count is unknown: no
  // "you haven't done one yet" to someone who may have done both.
  const { data: filed = [], isSuccess: fileLoaded } = useMissionFile();
  const challengingDone = filed.filter(
    (m) => tierOfLevel(m.level) === "challenging",
  ).length;
  const improbableLocked =
    getOpt("Diff3Unlocked") !== "1" && challengingDone < CHALLENGING_TO_UNLOCK;
  const lockMessage = fileLoaded
    ? `${lockLine} ${unlockProgress(challengingDone)}`
    : lockLine;

  // which case is picked up off the desk (index into `folders`)
  const [opened, setOpened] = useState<number | null>(null);
  // the code went out to the number on file, not one they typed
  const [unasked, setUnasked] = useState(false);
  const attempt = useRef(0);
  useEffect(
    () => () => {
      attempt.current += 1;
    },
    [],
  );
  const [arrived, setArrived] = useState(false);
  // the stage's size, which placeOnDesk lays the cases out in
  const deskRef = useRef<HTMLDivElement>(null);
  const [deskSize, setDeskSize] = useState<{ w: number; h: number } | null>(
    null,
  );
  useLayoutEffect(() => {
    const el = deskRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) =>
      setDeskSize({ w: e.contentRect.width, h: e.contentRect.height }),
    );
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  // still at the desk, nothing picked: point at the cases (again each time
  // they come back to it)
  const [hint, setHint] = useState(false);
  useEffect(() => {
    if (opened !== null) return;
    const t = setTimeout(() => setHint(true), HINT_MS);
    return () => {
      clearTimeout(t);
      setHint(false);
    };
  }, [opened]);
  const [stage, setStage] = useState<Stage>("code");
  // signed in as a returning operative: their standing, while we ask
  const [back, setBack] = useState<SignedIn | null>(null);
  const goTo = useStepperStore((s) => s.goTo);
  const carryOn = () => {
    setBack(null);
    setStage("granted");
  };
  // back to the mission they already have open, its clock where it was
  const resume = (m: NonNullable<SignedIn["current"]>) => {
    takeUpMission(m);
    setBack(null);
    goTo("Delivered");
  };
  const openCase = useFunnelStore((s) => s.openCase);
  // the open cases come next: have them in hand before a case is tapped
  useEffect(() => {
    Object.values(OPEN_CASES).forEach((src) => {
      const img = new Image();
      img.decoding = "async";
      img.src = src;
    });
  }, []);

  const qc = useQueryClient();
  // the attempt a verify was sent under (see onSignedIn)
  const verifyFrom = useRef(0);
  const { data: hasPhone } = useHasPhone(true);
  const login = usePhoneLogin({
    successMessage: "Verified.",
    showCodeSentToast: false,
    onSignedIn: (who) => {
      // Signed in either way, so the cached session and file are stale even if
      // they have left the panel since.
      void qc.invalidateQueries({ queryKey: ["secretmissions", "session"] });
      void qc.invalidateQueries({ queryKey: fileKeys.all });
      // A returning operative's doors were dealt to this browser, not to their
      // account: recompute them before accept resolves the MissionID.
      if (who?.claimed) void qc.invalidateQueries({ queryKey: doorKeys.today });
      // Left the panel (or the step) while the code was checked: nothing to open.
      if (verifyFrom.current !== attempt.current) return;
      // A browser that did not know them, and the number turns out to have a
      // mission open: offer it back before going on.
      if (!hasPhone && who?.claimed && who.current) {
        setBack(who);
        return;
      }
      setStage("granted");
    },
  });
  const verify = (e: FormEvent) => {
    verifyFrom.current = attempt.current;
    return login.verify(e);
  };
  useEffect(() => {
    if (stage !== "granted") return;
    // The code field had the keyboard up, and it is disabled while still
    // focused — so its blur (which scrolls back) never runs and the page is
    // left shoved up over a blank strip. Close the keyboard and put it back.
    (document.activeElement as HTMLElement | null)?.blur();
    requestAnimationFrame(() => window.scrollTo(0, 0));
    const t = setTimeout(() => {
      // again once the keyboard has finished sliding away
      window.scrollTo(0, 0);
      // unlocked: the open case is the next step (WebSmCase), so a refresh
      // there never lands back on the locked case
      if (opened === null) return;
      openCase(TIERS[opened]);
      dispatch(`CLICK${opened + 1}`);
    }, GRANTED_MS);
    return () => clearTimeout(t);
    // fires once, as ACCESS GRANTED shows
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);
  /** Tap a case: a sealed one gets waved off; any other goes to its code
   *  panel — for a known operative the code is texted to the number on file. */
  // Once the code has gone out, the number step becomes the panel, where it
  // is keyed in; "use a different number" clears the attempt and goes back.
  const step: Stage =
    stage === "phone" && login.attemptId != null ? "code" : stage;
  const pick = (i: number) => {
    if (!arrived || (opened !== null && !folders[opened].locked)) return;
    const currentAttempt = ++attempt.current;
    login.changeNumber();
    setOpened(i);
    // the sealed case: the agent waves it off; no code goes out for it
    if (i === 2 && improbableLocked) return;
    // New to this browser and not from one of our texts: ask for the number.
    if (!hasPhone && !opLink()) {
      setUnasked(false);
      setStage("phone");
      return;
    }
    // Known operative (e.g. came in from one of our texts): still a code to
    // open the case, but it goes to the number on file — no number to type.
    // If that fails, ask for the number after all.
    setStage("code");
    setUnasked(true);
    void login.sendCodeToKnown().then((sent) => {
      if (currentAttempt !== attempt.current) return;
      if (!sent) {
        setUnasked(false);
        setStage("phone");
      }
    });
  };
  const sendTypedNumber = async (e: FormEvent) => {
    e.preventDefault();
    if (!login.phoneValid || login.busy) return;
    const currentAttempt = ++attempt.current;
    const sending = login.sendCode(e);
    // Mount/focus the real code input during the submit gesture, before the
    // network wait: iOS can then open its numeric keyboard without another tap.
    flushSync(() => {
      setUnasked(false);
      setStage("code");
    });
    const sent = await sending;
    if (currentAttempt === attempt.current && !sent) setStage("phone");
  };
  const backToDesk = () => {
    attempt.current += 1;
    (document.activeElement as HTMLElement | null)?.blur();
    setOpened(null);
    setBack(null);
    setStage("phone");
    login.changeNumber();
    window.scrollTo(0, 0);
  };

  const folders: {
    label: string;
    event: string;
    /** Anchored to a side of the desk ("left" / "right" edge, or "center"),
     *  and from the BOTTOM of the desk band, so the cases spread to the
     *  visible edges on any shape of desk and spare desk sits above them. */
    pos: { anchor: "left" | "right" | "center"; inset: string; bottom: string };
    look: CaseLook;
    locked?: boolean;
  }[] = [
    {
      label: getOpt("Diff1Label") || "Standard",
      event: "CLICK1",
      pos: { anchor: "left", inset: "-2%", bottom: "67%" },
      // Sizes climb with difficulty (245 / 285 / 400): the first two side by
      // side at the back of the desk, the big one in front.
      look: {
        src: caseBeginner,
        hitShape: CASE_HIT_SHAPES.beginner,
        width: 245,
        tilt: -6,
        glow: "#ffd84a",
        aspect: 800 / 436,
        panel: {
          label: { left: 66.85, top: 72.39, width: 9.49, height: 3.61 },
          input: { left: 67.19, top: 77.36, width: 8.95, height: 3.98 },
        },
      },
    },
    {
      label: getOpt("Diff2Label") || "Challenging",
      event: "CLICK2",
      pos: { anchor: "right", inset: "-2%", bottom: "40%" },
      look: {
        src: caseChallenging,
        hitShape: CASE_HIT_SHAPES.challenging,
        width: 285,
        tilt: 5,
        glow: "#ff8a2a",
        aspect: 800 / 458,
        panel: {
          label: { left: 66.71, top: 72.94, width: 9.43, height: 3.18 },
          input: { left: 67.32, top: 77.76, width: 8.76, height: 3.76 },
        },
      },
    },
    {
      label: getOpt("Diff3Label") || "Mission Improbable",
      event: "CLICK3",
      locked: improbableLocked,
      pos: { anchor: "center", inset: "0%", bottom: "0%" },
      look: {
        src: caseImprobable,
        hitShape: CASE_HIT_SHAPES.improbable,
        width: 400,
        tilt: 0,
        glow: "#52c2ff",
        aspect: 800 / 438,
        panel: {
          label: { left: 66.85, top: 72.57, width: 9.28, height: 3.24 },
          input: { left: 67.26, top: 77.43, width: 8.94, height: 3.87 },
        },
      },
    },
  ];

  const sectionVars = {
    ...stepVars({ accent, bg }),
    "--sm-bg": bg,
  } as CSSProperties;
  const open = opened === null ? null : folders[opened];
  const casePress = usePressGroup(pick, !arrived || (!!open && !open.locked));
  return (
    <section
      style={sectionVars}
      className="relative h-[100dvh] overflow-clip font-serif bg-[var(--sm-bg)]"
    >
      {/* the handler at their desk; the files land on the desk's near half */}
      <img
        src={open?.locked ? notYet : desk}
        alt=""
        className="absolute inset-0 h-full w-full object-cover object-[50%_100%]"
      />
      {/* keep the title legible over the room */}
      <div className="absolute inset-x-0 top-0 h-[22%] bg-[linear-gradient(180deg,rgba(0,0,0,.72),transparent)]" />

      {hint && opened === null ? (
        <div className="pointer-events-none absolute inset-x-0 top-[max(108px,15dvh)] z-[6] flex animate-[smSay_.45s_cubic-bezier(.2,.8,.3,1)_both] flex-col items-center gap-2">
          <span className="rounded-full border border-[#d6a94e]/60 bg-black/75 px-4 py-2 font-stencil text-[13px] tracking-[3px] text-[#eadfc6] uppercase [text-shadow:0_0_14px_rgba(214,169,78,.5)]">
            Tap a case to choose it
          </span>
          <ChevronDown
            strokeWidth={3}
            className="size-8 animate-[smHintBob_1.1s_ease-in-out_infinite] text-[#d6a94e] [filter:drop-shadow(0_0_8px_#d6a94e)]"
          />
        </div>
      ) : null}

      {/* same header treatment as the Doors step */}
      <div className="pointer-events-none absolute top-[26px] right-0 left-0 z-[6] px-5 text-center">
        <div className="mb-[9px] font-stencil text-[10.5px] tracking-[4px] text-[#d6a94e] uppercase [text-shadow:0_0_14px_rgba(214,169,78,.5)]">
          ▸ {title} ◂
        </div>
        <div className="mx-auto max-w-[460px] font-stencil text-[clamp(13px,3.6cqw,17px)] leading-[1.35] tracking-[1.5px] text-[#eadfc6] uppercase [text-shadow:0_0_18px_rgba(214,169,78,.3),0_2px_14px_rgba(0,0,0,.9)]">
          {prompt}
        </div>
        <div className="mx-auto mt-3 h-px w-11 bg-[linear-gradient(90deg,transparent,#d6a94e,transparent)]" />
      </div>

      {/* The photo's own frame (see PHOTO_BOX): the desk plane and the speech
          bubble are placed in picture coordinates, so they stay on the desk and
          at her mouth whatever the screen. */}
      <div
        style={PHOTO_BOX}
        className="pointer-events-none absolute bottom-0 left-1/2 z-[3] -translate-x-1/2 [container-type:inline-size]"
      >
        {/* The plane spans the part of the photo that is actually on screen:
            the whole photo when it fits the frame's width, otherwise the frame
            width (the photo's sides are cropped off on a narrow phone). 100cqw
            is the photo's drawn width. */}
        <div
          inert={!!open && !open.locked}
          style={{
            top: deskTop,
            left: "max(0px, (100cqw - 100vw) / 2)",
            right: "max(0px, (100cqw - 100vw) / 2)",
          }}
          className="pointer-events-none absolute bottom-[2%] [container-type:size]"
        >
          {/* The stage: the reference phone's desk shape (REF_BAND), fitted
              inside the visible desk and centred at its front edge. The cases
              are placed on it by placeOnDesk (flat, no 3D), so the
              arrangement is identical everywhere, only scaled. */}
          <div
            ref={deskRef}
            style={{ width: "100cqw", height: "100cqh" }}
            className="pointer-events-none absolute bottom-0 left-1/2 -translate-x-1/2"
          >
            {deskSize
              ? folders.map((f, i) => {
                  const at = placeOnDesk(deskSize.w, deskSize.h, f.look, f.pos);
                  return (
                    <div
                      key={f.event}
                      style={
                        {
                          left: at.left,
                          bottom: at.bottom,
                          // the sealed case under the others: where the
                          // see-through corners of the pictures overlap, the
                          // tap is the open case's (they never visibly overlap)
                          zIndex: f.locked ? 0 : i + 1,
                          "--f-delay": `${i * 0.42}s`,
                        } as CSSProperties
                      }
                      className="pointer-events-none absolute animate-[smFileIn_.6s_cubic-bezier(.3,.9,.35,1)_var(--f-delay)_both]"
                      onAnimationEnd={(e) => {
                        if (
                          i === 2 &&
                          e.target === e.currentTarget &&
                          e.animationName === "smFileIn"
                        )
                          setArrived(true);
                      }}
                    >
                      {/* once a case is picked, the others leave the desk (on
                          a child: the fly-in animation holds the opacity) */}
                      <div
                        className={`transition-opacity duration-500 ${
                          open && !open.locked && opened !== i
                            ? "opacity-0"
                            : ""
                        }`}
                      >
                        <Case
                          caseIndex={i}
                          look={f.look}
                          label={f.label}
                          width={at.width}
                          disabled={!arrived || (!!open && !open.locked)}
                          press={casePress}
                        />
                      </div>
                    </div>
                  );
                })
              : null}
          </div>
        </div>

        {/* the sealed file: the handler waves it off. No dimming and no cover —
            the desk, its files and the title all stay where they were. */}
        {open?.locked ? (
          <div
            data-no-case
            style={{ top: bubble.top }}
            className="pointer-events-auto absolute right-0 left-0 z-[5] flex flex-col items-center"
          >
            <div className="relative w-[min(380px,calc(100vw-40px))] animate-[smSay_.45s_cubic-bezier(.2,.8,.3,1)_both] rounded-[6px] bg-[#f3ecd8] px-5 py-4 text-center text-[#2b2107] shadow-[0_22px_50px_rgba(0,0,0,.75)]">
              {/* the no-handler agent is unnamed until a handler is picked */}
              {handler ? (
                <div className="mb-1.5 font-mono text-[10px] tracking-[.24em] text-[#8a7350] uppercase">
                  {handler}
                </div>
              ) : null}
              <div className="text-[clamp(13px,3.9cqw,16px)] leading-[1.32] font-medium text-pretty">
                {lockMessage}
              </div>
              {/* tail, pointing up at whoever is speaking */}
              <div
                style={{ left: bubble.tailLeft }}
                className="absolute -top-[26px] size-0 -translate-x-1/2 border-x-[11px] border-b-[27px] border-x-transparent border-b-[#f3ecd8]"
              />
            </div>
            <button
              type="button"
              onClick={backToDesk}
              // Inline, not classes: ACTION_BTN_CLASS sets colour and border
              // through the same arbitrary-value utilities, and Tailwind's own
              // output order — not the class list order — would decide the
              // winner. This button sits under a bright cream bubble, so it is
              // lifted a stop to keep reading as enabled.
              style={{
                background: "linear-gradient(180deg,#9c2a21,#6b1a16)",
                borderColor: "rgba(232,190,110,.75)",
                color: "#fbeec4",
              }}
              className={`mt-4 ${ACTION_BTN_CLASS} rounded-[9px] border-2 px-7 py-3 text-[16px] font-bold tracking-[.3px] shadow-[0_12px_30px_rgba(0,0,0,.6),0_0_26px_rgba(232,190,110,.45),inset_0_1px_0_rgba(255,255,255,.16)]`}
            >
              {changeLabel}
            </button>
          </div>
        ) : null}
      </div>

      {back ? (
        <div
          data-no-case
          className="absolute inset-0 z-[9] grid place-items-center bg-black/75 p-5 font-sans"
        >
          <div className="w-full max-w-[340px] -rotate-[1deg] animate-[smSay_.35s_cubic-bezier(.2,.8,.3,1)_both] bg-[#fbf6e6] px-6 pt-6 pb-5 text-center text-[#2b2107] shadow-[0_20px_50px_rgba(0,0,0,.7)]">
            <div className="font-mono text-[11px] tracking-[.24em] text-[#8a7350] uppercase">
              Welcome back, operative
            </div>
            <>
              <p className="mt-2 font-serif text-[17px] leading-snug">
                You're in the middle of
              </p>
              <div className="mt-1 font-stencil text-[22px] leading-tight tracking-[.04em] text-[#a8271c] uppercase">
                {back.current?.name}
              </div>
              <p className="mt-1 font-serif text-[16px] leading-snug italic">
                Mission {(back.filed ?? 0) + 1}. Pick up where you left off?
              </p>
              <button
                type="button"
                onClick={() => resume(back.current!)}
                className={`mt-4 w-full ${ACTION_BTN_CLASS} rounded-[9px] px-5 py-3 text-[15.5px] font-bold`}
              >
                Continue that mission
              </button>
              <button
                type="button"
                onClick={carryOn}
                className="mt-2 w-full cursor-pointer py-2 text-[14px] text-[#5b4a2a] underline underline-offset-2"
              >
                Choose a new one
              </button>
            </>
          </div>
        </div>
      ) : null}

      {open && !open.locked && step === "phone" ? (
        <PhoneModal
          login={login}
          onBack={backToDesk}
          onSubmit={sendTypedNumber}
        />
      ) : null}
      {open && !open.locked && (step === "code" || step === "granted") ? (
        <CodePanel
          look={open.look}
          stage={step}
          login={login}
          unasked={unasked}
          onBack={backToDesk}
          onVerify={verify}
          onChangeNumber={() => {
            attempt.current += 1;
            login.changeNumber();
            setUnasked(false);
            setStage("phone");
          }}
        />
      ) : null}

      {/* No underscore in the names: Tailwind reads `_` in an arbitrary value
          as a space, so `sm_fileIn` would never match a keyframe. */}
      <style>{`
        @keyframes smFileIn{0%{opacity:0;transform:translate(-160%,-6%) rotate(-16deg)}60%{opacity:1}80%{transform:translate(4%,0) rotate(3deg)}100%{opacity:1;transform:translate(0,0) rotate(0deg)}}
        @keyframes smDim{from{opacity:0}to{opacity:1}}
        @keyframes smZoom{from{opacity:0;transform:scale(.22)}to{opacity:1;transform:none}}
        @keyframes smBlink{to{opacity:.25}}
        @keyframes smSay{from{opacity:0;transform:translateY(10px) scale(.96)}to{opacity:1;transform:none}}
        @keyframes smHintBob{0%,100%{translate:0 0}50%{translate:0 30%}}
      `}</style>
    </section>
  );
}
